import { MAX_OUTPUT_MEGAPIXELS } from "../../functions/src/shared/upscaleLimits";

export type UpscaleFactor = "x2" | "x3" | "x4" | "x5" | "x6";

/** Which upscaler tier to run. Mirrors `functions/src/ai/types.ts`. */
export type Upscaler = "seedvr" | "topaz";

export const UPSCALERS: readonly Upscaler[] = ["seedvr", "topaz"];

/** fal endpoints behind each tier — analytics labels only. */
export const UPSCALER_MODELS: Record<Upscaler, string> = {
  seedvr: "fal-ai/seedvr/upscale/image",
  topaz: "fal-ai/topaz/upscale/image",
};

/** Factors offered per tier. Topaz's API caps at x4. */
export const UPSCALE_FACTORS_BY_UPSCALER: Record<
  Upscaler,
  readonly UpscaleFactor[]
> = {
  seedvr: ["x2", "x3", "x4", "x5", "x6"],
  topaz: ["x2", "x3", "x4"],
};

/** Shared with backend execution and workflow cost confirmation. */
export { MAX_OUTPUT_MEGAPIXELS } from "../../functions/src/shared/upscaleLimits";
export const MAX_UPSCALE_OUTPUT_PIXELS = MAX_OUTPUT_MEGAPIXELS * 1_000_000;

/**
 * SeedVR2 refuses images beyond ~10k px on the long edge. Under the megapixel
 * cap this only binds on panoramic sources (wider than ~2.4:1). Topaz has no
 * such limit at our sizes.
 */
export const SEEDVR_MAX_LONG_EDGE = 10_000;

export function getUpscaleMultiplier(upscaleFactor: UpscaleFactor): number {
  return Number(upscaleFactor.slice(1));
}

/** Why a factor is unavailable — drives which hint the UI shows. */
export type UpscaleLimitReason = "megapixels" | "longEdge" | "unsupported";

export function calculateUpscaleViability(
  width: number | null | undefined,
  height: number | null | undefined,
  upscaleFactor: UpscaleFactor,
  upscaler: Upscaler = "seedvr"
): {
  dimensionsLoaded: boolean;
  available: boolean;
  targetWidth: number | null;
  targetHeight: number | null;
  targetMegapixels: number | null;
  limitReason: UpscaleLimitReason | null;
} {
  if (!UPSCALE_FACTORS_BY_UPSCALER[upscaler].includes(upscaleFactor)) {
    return {
      dimensionsLoaded: true,
      available: false,
      targetWidth: null,
      targetHeight: null,
      targetMegapixels: null,
      limitReason: "unsupported",
    };
  }

  if (!width || !height || width <= 0 || height <= 0) {
    return {
      dimensionsLoaded: false,
      available: false,
      targetWidth: null,
      targetHeight: null,
      targetMegapixels: null,
      limitReason: null,
    };
  }

  const scale = getUpscaleMultiplier(upscaleFactor);
  const targetWidth = width * scale;
  const targetHeight = height * scale;
  const targetMegapixels = (width * height * scale * scale) / 1_000_000;

  const exceedsMegapixels = targetMegapixels > MAX_OUTPUT_MEGAPIXELS;
  const exceedsLongEdge =
    upscaler === "seedvr" &&
    Math.max(targetWidth, targetHeight) > SEEDVR_MAX_LONG_EDGE;

  return {
    dimensionsLoaded: true,
    available: !exceedsMegapixels && !exceedsLongEdge,
    targetWidth,
    targetHeight,
    targetMegapixels,
    limitReason: exceedsMegapixels
      ? "megapixels"
      : exceedsLongEdge
        ? "longEdge"
        : null,
  };
}

/**
 * Check whether an image can be upscaled at the given factor without
 * exceeding the output limits of the chosen upscaler. Returns the matching
 * error code plus a technical message (Sentry only — the user-facing text
 * comes from the `errors.<code>` translation) if a limit would be exceeded,
 * or `null` if the upscale is safe.
 *
 * The code must track `limitReason`: the megapixel cap and SeedVR's long-edge
 * limit have different remedies ("smaller factor" vs. "use the quality
 * upscaler"), and this pre-check runs before the backend can answer with its
 * own code.
 */
export function getUpscaleLimitError(
  width: number,
  height: number,
  upscaleFactor: UpscaleFactor,
  upscaler: Upscaler = "seedvr"
): { code: string; message: string } | null {
  const viability = calculateUpscaleViability(
    width,
    height,
    upscaleFactor,
    upscaler
  );
  if (!viability.dimensionsLoaded || viability.available) return null;

  if (viability.limitReason === "unsupported") {
    return {
      code: "generation/upscale_resolution_limit",
      message: `${upscaler} does not support ${upscaleFactor}.`,
    };
  }
  if (viability.limitReason === "longEdge") {
    return {
      code: "generation/upscale_long_edge_limit",
      message: `Upscaled image would be ${viability.targetWidth}×${viability.targetHeight}, whose long edge exceeds the ${SEEDVR_MAX_LONG_EDGE}px limit of the standard upscaler. Try a lower upscale factor or the quality upscaler.`,
    };
  }
  const outputMp = viability.targetMegapixels?.toFixed(1);
  const maxMp = MAX_OUTPUT_MEGAPIXELS.toFixed(0);
  return {
    code: "generation/upscale_resolution_limit",
    message: `Upscaled image would be ${outputMp}MP (${viability.targetWidth}×${viability.targetHeight}), which exceeds the maximum of ${maxMp}MP. Try a lower upscale factor or a smaller source image.`,
  };
}
